import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './ResourceDetails.css';

const ResourceDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<'preview' | 'description'>('preview');

  // Mock data
  const resource = {
    id: 1,
    title: 'Mathematics Fundamentals',
    author: 'John Doe',
    type: '📖 Book',
    rating: 4.8,
    price: 19,
    originalPrice: 29,
    views: 1250,
    pages: 350,
    language: 'English',
    publishedDate: '2023-01-15',
    isbn: '978-0-123456-78-9',
    description: `This comprehensive guide to mathematics fundamentals covers all essential topics from basic arithmetic to advanced calculus. Perfect for students at all levels, this book provides clear explanations, practical examples, and step-by-step solutions to help you master mathematical concepts.

Key Features:
• Comprehensive coverage of mathematical concepts
• Clear explanations with real-world examples
• Practice problems with detailed solutions
• Suitable for beginners and advanced learners
• Includes tips and tricks for problem-solving

Whether you're preparing for exams, improving your skills, or just curious about mathematics, this book is an invaluable resource that will guide you through your mathematical journey.`,
    previewUrl: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', // Sample PDF URL
    downloadUrl: '#',
    category: 'Mathematics'
  };

  const handlePurchase = () => {
    // Navigate to payment page or show payment popup
    console.log('Purchase clicked');
  };

  return (
    <div className="resource-details-page">
      <header className="resource-details-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1>Resource Details</h1>
        <button className="share-button">📤</button>
      </header>

      <div className="resource-details-content">
        {/* Resource Header */}
        <div className="resource-header-section">
          <div className="resource-cover">
            <div className="resource-cover-icon">{resource.type}</div>
          </div>
          <div className="resource-header-info">
            <div className="resource-category-badge">{resource.category}</div>
            <h2 className="resource-title-large">{resource.title}</h2>
            <p className="resource-author-large">by {resource.author}</p>
            <div className="resource-rating-large">
              <span className="stars">⭐⭐⭐⭐⭐</span>
              <span className="rating-value">{resource.rating}</span>
              <span className="rating-count">({resource.views} views)</span>
            </div>
          </div>
        </div>

        {/* Resource Stats */}
        <div className="resource-stats">
          <div className="stat-item">
            <span className="stat-icon">👁️</span>
            <span className="stat-value">{resource.views.toLocaleString()}</span>
            <span className="stat-label">Views</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">📄</span>
            <span className="stat-value">{resource.pages}</span>
            <span className="stat-label">Pages</span>
          </div>
          <div className="stat-item">
            <span className="stat-icon">🌐</span>
            <span className="stat-value">{resource.language}</span>
            <span className="stat-label">Language</span>
          </div>
        </div>

        {/* Price Section */}
        <div className="resource-price-section">
          {resource.originalPrice && (
            <span className="original-price">${resource.originalPrice}</span>
          )}
          <span className="current-price">${resource.price}</span>
          <button className="purchase-button" onClick={handlePurchase}>
            Purchase Now
          </button>
        </div>

        {/* Tabs */}
        <div className="resource-tabs">
          <button
            className={`resource-tab ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            Preview
          </button>
          <button
            className={`resource-tab ${activeTab === 'description' ? 'active' : ''}`}
            onClick={() => setActiveTab('description')}
          >
            Description
          </button>
        </div>

        {/* Tab Content */}
        <div className="resource-tab-content">
          {activeTab === 'preview' && (
            <div className="preview-content">
              <div className="pdf-viewer-container">
                <iframe
                  src={`${resource.previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="pdf-viewer"
                  title="PDF Preview"
                />
                <div className="pdf-viewer-overlay">
                  <p className="preview-note">This is a preview. Purchase to access the full content.</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'description' && (
            <div className="description-content">
              <div className="description-text">
                {resource.description.split('\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              
              <div className="resource-details-info">
                <h3>Additional Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Author:</span>
                    <span className="info-value">{resource.author}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Published:</span>
                    <span className="info-value">{new Date(resource.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Pages:</span>
                    <span className="info-value">{resource.pages}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Language:</span>
                    <span className="info-value">{resource.language}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">ISBN:</span>
                    <span className="info-value">{resource.isbn}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Category:</span>
                    <span className="info-value">{resource.category}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default ResourceDetails;
